import * as React from 'react';
import { Query } from 'react-apollo';
import { ApolloQueryResult } from 'apollo-client';
import { findMatchingRecord } from 'utils/arrayHelpers';

import ToastActions from 'actions/ToastActions';
import LinkConstants from 'constants/LinkConstants';

import {
  UnclaimedGroupProfile,
  FullUnclaimedGroupProfileType,
  PartialUnclaimedGroupProfileType,
  GroupMember,
} from 'groups/components/UnclaimedGroupProfile';
import UNCLAIMED_GROUP_PROFILE_QUERY from './queries/UnclaimedGroupProfileQuery.gql';
// @ts-ignore
import { UserProfile } from 'users/components/UsersProfile';
import { OrganizationRole } from 'globalTypes';

import {
  UnclaimedGroupProfileQuery,
  UnclaimedGroupProfileQueryVariables,
  UnclaimedGroupProfileQuery_unclaimedGroups,
  UnclaimedGroupProfileQuery_unclaimedGroups_owners,
  UnclaimedGroupProfileQuery_unclaimedGroups_participants,
} from './queries/__generated__/UnclaimedGroupProfileQuery';

type UnclaimedGroupProfileQuery_member =
  | UnclaimedGroupProfileQuery_unclaimedGroups_owners
  | UnclaimedGroupProfileQuery_unclaimedGroups_participants;

type Props = {
  group: PartialUnclaimedGroupProfileType;
  onBackClicked?: () => void;
  onClose: () => void;
  onUserChanged: () => void;
  onUserClicked: (user: UserProfile, role: OrganizationRole) => void;
  reloadQuery: boolean;
};

export default class UnclaimedGroupProfileContainer extends React.Component<Props> {
  _refetch: (() => Promise<ApolloQueryResult<UnclaimedGroupProfileQuery>>) | null = null;

  componentDidUpdate(prevProps: Props) {
    const { reloadQuery } = this.props;
    const refetch = this._refetch;

    if (reloadQuery && !prevProps.reloadQuery && refetch) {
      refetch();
    }
  }

  renderError = () => {
    const { onBackClicked, onClose } = this.props;

    ToastActions.showErrorToast(
      `There was an error. Please try again, or email
      ${LinkConstants.SUPPORT_EMAIL}
      for help.`
    );

    if (onBackClicked) {
      onBackClicked();
    } else {
      onClose();
    }
  };

  translateMember(member: UnclaimedGroupProfileQuery_member): GroupMember {
    return {
      ...member,
      roles: member.role ? [member.role as OrganizationRole] : [],
    };
  }

  translateGroup(group: UnclaimedGroupProfileQuery_unclaimedGroups): FullUnclaimedGroupProfileType {
    return {
      ...group,
      owners: group.owners.map((member: UnclaimedGroupProfileQuery_member) =>
        this.translateMember(member)
      ),
      members: group.participants.map((member: UnclaimedGroupProfileQuery_member) =>
        this.translateMember(member)
      ),
      membershipsCount: group.participantCount,
    };
  }

  findUnclaimedGroup(data?: UnclaimedGroupProfileQuery): FullUnclaimedGroupProfileType | null {
    if (!data) {
      return null;
    }

    const { group } = this.props;
    const unclaimedGroup = findMatchingRecord<UnclaimedGroupProfileQuery_unclaimedGroups>(
      data.unclaimedGroups,
      group.uuid
    );

    if (unclaimedGroup) {
      return this.translateGroup(unclaimedGroup);
    } else {
      return null;
    }
  }

  render() {
    const { group, onBackClicked, onClose, onUserChanged, onUserClicked } = this.props;

    return (
      <Query<UnclaimedGroupProfileQuery, UnclaimedGroupProfileQueryVariables>
        query={UNCLAIMED_GROUP_PROFILE_QUERY}
        variables={{ groupUuid: group.uuid }}>
        {({ data, error, loading, refetch }) => {
          if (refetch) {
            this._refetch = refetch;
          }

          if (error) {
            this.renderError();
          }

          const unclaimedGroup = this.findUnclaimedGroup(data);

          return (
            <UnclaimedGroupProfile
              partialGroup={group}
              loadedGroup={unclaimedGroup}
              loading={loading}
              onBackClicked={onBackClicked}
              onClose={onClose}
              onUserChanged={onUserChanged}
              onUserClicked={onUserClicked}
            />
          );
        }}
      </Query>
    );
  }
}
